import { Layout } from "@readium/shared";
const MAX_CHUNK_LENGTH = 600;
// Arabic and Urdu question marks and full stops, CJK full stops: the end of a sentence too.
const SENTENCE_END = /(?<=[.!?…»؟۔。！？])\s+/;
const BLOCKS = "p, div, br, h1, h2, h3, h4, h5, h6, li, tr, blockquote, section, article, aside, header, footer, figure, figcaption, pre, hr";
/** The text of an XHTML chapter, one line per block (paragraph, heading…). */
export function chapterLines(markup) {
    const parse = (type) => new DOMParser().parseFromString(markup, type);
    let doc = parse("application/xhtml+xml");
    if (doc.querySelector("parsererror"))
        doc = parse("text/html");
    doc.querySelectorAll("script, style, head").forEach((node) => node.remove());
    doc.querySelectorAll(BLOCKS).forEach((node) => {
        node.before("\n");
        node.after("\n");
    });
    return linesOf(doc.body?.textContent ?? "");
}
const LIGATURES = { "\ufb00": "ff", "\ufb01": "fi", "\ufb02": "fl", "\ufb03": "ffi", "\ufb04": "ffl", "\ufb05": "st", "\ufb06": "st" };
// Private use areas (glyphs of a font that mean nothing as text), controls and invisible marks: not text.
const NOT_TEXT = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f-\u009f\u00ad\u200b-\u200f\u2028-\u202e\u2060-\u2064\ue000-\uf8ff\ufeff\ufff9-\uffff]|[\u{f0000}-\u{10ffff}]/gu;
/** Lines of text, spaces folded, empty lines left out, the glyphs without meaning taken away. */
export function linesOf(text) {
    return text
        .split("\n")
        .map((line) => line
        .replace(/[\ufb00-\ufb06]/g, (char) => LIGATURES[char] ?? char)
        .replace(NOT_TEXT, "")
        .replace(/[ \t\u00a0\u202f\r\f]+/g, " ")
        .trim())
        .filter((line) => line.length > 0);
}
/** A fixed-layout book (pages are images of the print): nothing to read aloud. */
export function isReflowable(publication) {
    return publication.metadata.effectiveLayout !== Layout.fixed;
}
/** The text cut for the voice: a chunk per line, a long line cut at the end of a sentence. */
export function splitIntoChunks(lines) {
    const length = lines.reduce((sum, line) => sum + line.length + 1, 0);
    const chunks = [];
    let offset = 0;
    for (const line of lines) {
        const lineStart = offset;
        offset += line.length + 1;
        if (!line.trim())
            continue;
        if (line.length <= MAX_CHUNK_LENGTH) {
            chunks.push({ text: line, start: lineStart / length });
            continue;
        }
        let current = "";
        let currentStart = lineStart;
        let cursor = lineStart;
        for (const sentence of line.split(SENTENCE_END)) {
            if (current && current.length + sentence.length + 1 > MAX_CHUNK_LENGTH) {
                chunks.push({ text: current, start: currentStart / length });
                current = "";
                currentStart = cursor;
            }
            current = current ? `${current} ${sentence}` : sentence;
            cursor += sentence.length + 1;
        }
        if (current)
            chunks.push({ text: current, start: currentStart / length });
    }
    return chunks;
}
// Three-letter codes some books give (ISO 639-2): the two-letter one the voices go by.
const THREE_LETTERS = {
    ara: "ar", fra: "fr", fre: "fr", eng: "en", spa: "es", deu: "de", ger: "de", ita: "it", por: "pt", nld: "nl", dut: "nl",
    rus: "ru", zho: "zh", chi: "zh", jpn: "ja", kor: "ko", heb: "he", fas: "fa", per: "fa", urd: "ur", tur: "tr", ell: "el",
    gre: "el", hin: "hi", tha: "th", pol: "pl", ukr: "uk",
};
/** The two-letter language of the book, for the voice ("fr" when the book does not say). */
export function bookLanguage(publication) {
    const first = publication.metadata.languages?.[0];
    const code = first?.split(/[-_]/)[0]?.toLowerCase();
    if (code && code.length === 3 && THREE_LETTERS[code])
        return THREE_LETTERS[code];
    return code && code.length === 2 ? code : "fr";
}
/** The writings that tell the language of a text, with the languages written in them (the most read first). */
const WRITINGS = [
    [/\p{Script=Arabic}/gu, ["ar", "fa", "ur"]],
    [/\p{Script=Hebrew}/gu, ["he", "yi"]],
    [/\p{Script=Cyrillic}/gu, ["ru", "uk", "bg", "sr"]],
    [/\p{Script=Greek}/gu, ["el"]],
    [/[\p{Script=Hiragana}\p{Script=Katakana}]/gu, ["ja"]],
    [/\p{Script=Hangul}/gu, ["ko"]],
    [/\p{Script=Han}/gu, ["zh", "ja"]],
    [/\p{Script=Devanagari}/gu, ["hi"]],
    [/\p{Script=Thai}/gu, ["th"]],
];
/**
 * The language a text is read in: `expected` (the book's, the conversation's), unless the text is mostly in a writing
 * that language does not use (an answer in Arabic while the voice would be French reads nothing): then the main
 * language of that writing.
 */
export function speechLanguage(text, expected) {
    const letters = text.match(/\p{L}/gu)?.length ?? 0;
    if (!letters)
        return expected;
    const code = expected.toLowerCase().split(/[-_]/)[0] ?? expected;
    for (const [writing, languages] of WRITINGS) {
        // Kana, even a few among the Han characters, is Japanese.
        const share = (text.match(writing)?.length ?? 0) / letters;
        if (share > 0.5 || (languages[0] === "ja" && share > 0.05))
            return languages.includes(code) ? code : languages[0];
    }
    return code;
}
/** Where a chunk is in its chapter, for the page to follow the voice: its first words and what comes before. */
export function chunkLocator(href, type, chunk, previous) {
    const split = Math.min(chunk.text.length, 60);
    return {
        href,
        type,
        locations: { progression: chunk.start },
        text: { before: previous?.text.slice(-32) ?? "", highlight: chunk.text.slice(0, split), after: chunk.text.slice(split, split + 60) },
    };
}
/**
 * Reads the book aloud from the page on screen to the end, chapter after chapter, and turns the pages along the
 * voice. Resolves at the end of the book (true), when the voice fails (false) or when `signal` is aborted.
 */
export async function readAloud(reader, loadLines, speech, language, signal) {
    const files = reader.publication.readingOrder.items;
    const here = reader.currentLocator;
    let index = files.findIndex((file) => file.href === here.href.split("#")[0]);
    if (index < 0)
        return false;
    let from = here.locations?.progression ?? 0;
    while (index < files.length && !signal.aborted) {
        const file = files[index];
        const type = file.type ?? "application/xhtml+xml";
        let lines = [];
        try {
            lines = await loadLines(file.href);
        }
        catch {
            // A file that cannot be read is skipped.
        }
        if (signal.aborted)
            return true;
        const chunks = splitIntoChunks(lines);
        // From the first paragraph that starts after the place of the page (the whole chapter after a turn).
        let first = from <= 0 ? 0 : chunks.findIndex((chunk) => chunk.start >= from);
        if (first < 0)
            first = chunks.length;
        for (let i = first; i < chunks.length; i++) {
            // The page follows the voice; a page that cannot be found does not stop it.
            await reader.goTo(chunkLocator(file.href, type, chunks[i], chunks[i - 1])).catch(() => false);
            if (signal.aborted)
                return true;
            if (!(await speech.speak(chunks[i].text, speechLanguage(chunks[i].text, language), signal)))
                return signal.aborted;
        }
        index += 1;
        from = 0;
        if (index < files.length && chunks.length > 0 && !signal.aborted) {
            if (!(await reader.goToHref(files[index].href).catch(() => false)))
                return false;
        }
    }
    return true;
}
/** The voices of the browser, which some browsers only list a moment after the page has loaded. */
function voices(synth) {
    const now = synth.getVoices();
    if (now.length)
        return Promise.resolve(now);
    return new Promise((resolve) => {
        const done = () => {
            synth.removeEventListener("voiceschanged", done);
            clearTimeout(timer);
            resolve(synth.getVoices());
        };
        const timer = setTimeout(done, 1500);
        synth.addEventListener("voiceschanged", done);
    });
}
/** The voice for a language: a voice of the device rather than one on a server, the default one if it fits. */
export function pickVoice(list, language) {
    const fits = list.filter((voice) => voice.lang.toLowerCase().replace("_", "-").split("-")[0] === language);
    const score = (voice) => (voice.localService ? 2 : 0) + (voice.default ? 1 : 0);
    return fits.sort((a, b) => score(b) - score(a))[0];
}
/** The utterance being spoken, held so that the browser does not forget it before its end. */
let current;
/** The text-to-speech voice of the browser, or undefined when it has none. */
export function browserSpeechPlayer() {
    if (typeof window === "undefined" || !("speechSynthesis" in window) || typeof SpeechSynthesisUtterance === "undefined")
        return undefined;
    const synth = window.speechSynthesis;
    return {
        async speak(text, language, signal) {
            const list = await voices(synth);
            const voice = pickVoice(list, language);
            // The browser lists its voices and none speaks the language of the book.
            if (list.length > 0 && !voice)
                return false;
            if (signal.aborted)
                return false;
            return new Promise((resolve) => {
                const utterance = new SpeechSynthesisUtterance(text);
                utterance.lang = voice?.lang ?? language;
                if (voice)
                    utterance.voice = voice;
                // Chrome forgets an utterance nothing holds on to and never says it has ended: the reading then stops after
                // the first paragraph. It is kept until it is over.
                current = utterance;
                let settled = false;
                // Chrome on a computer stops a voice of a server after some fifteen seconds unless it is nudged now and then;
                // on Android, a pause is a stop.
                const keepAlive = voice && !voice.localService && !/Android/i.test(navigator.userAgent)
                    ? setInterval(() => synth.speaking && (synth.pause(), synth.resume()), 10000)
                    : undefined;
                // The voice is silent and nothing is waiting, but the end was never told (some voices of Android and Safari):
                // the paragraph is over.
                const startedAt = Date.now();
                let silent = 0;
                const watchdog = setInterval(() => {
                    silent = !synth.speaking && !synth.pending && Date.now() - startedAt > 1500 ? silent + 1 : 0;
                    if (silent >= 2)
                        finish(true);
                }, 500);
                const finish = (completed) => {
                    if (settled)
                        return;
                    settled = true;
                    clearInterval(keepAlive);
                    clearInterval(watchdog);
                    if (current === utterance)
                        current = undefined;
                    signal.removeEventListener("abort", onAbort);
                    resolve(completed);
                };
                const onAbort = () => {
                    synth.cancel();
                    finish(false);
                };
                utterance.onend = () => finish(true);
                utterance.onerror = () => finish(false);
                signal.addEventListener("abort", onAbort);
                synth.cancel();
                synth.speak(utterance);
            });
        },
    };
}
//# sourceMappingURL=speech.js.map